/* istanbul ignore file */

import { useQuery } from "@apollo/react-hooks";
import { ApolloError } from "apollo-client";
import gql from "graphql-tag";
import * as React from "react";
import { Query as LegacyQuery } from "react-apollo";

export const userQuery = gql`
  query userQuery {
    viewer {
      id
      organisationId
      avatar
      email
      emailVerified
      lockeUserName
      spotReaId
      hasContract
      createdDate
      isToggledLCv3
      featureToggle {
        isListingV3FlowOpted
        isListingManagementV3WebEnabled
      }
      accountManager {
        avatar
        displayName
        email
        contact {
          agentEmail
          consumerEmail
          primaryPhone {
            number
            isWhatsApp
            phoneCode
            phoneCodeReference {
              id
              code
              description
              label
            }
          }
          secondaryPhone {
            number
            isWhatsApp
            phoneCode
            phoneCodeReference {
              id
              code
              description
              label
            }
          }
          didPhone {
            number
            isWhatsApp
            phoneCode
            phoneCodeReference {
              id
              code
              description
              label
            }
          }
        }
      }
      organisation {
        id
        name {
          en_GB
          zh_CN
          zh_HK
        }
        parent {
          id
          name {
            en_GB
            zh_HK
            zh_CN
          }
          allocationOn
        }
        isAgency
        isBranch
        allocationOn
        hasContract
      }
      firstName {
        en_GB
        zh_CN
        zh_HK
      }
      lastName {
        en_GB
        zh_CN
        zh_HK
      }
      fullName {
        en_GB
        zh_CN
        zh_HK
      }
      roleCodes {
        id
        code
        description
        label
      }
      capabilityPermission {
        canAccessBrickz
        canAccessIqAgent
        brickzLatestExpiryDate
      }
      capability {
        isAgentNetUser
      }
      permissions {
        canCreateRoleCodes {
          id
          code
          description
        }
        viewMyTeamMenu
        viewOrganisationMenu
        viewSubscriptionsMenu
        viewEnquiryManagementMenu
        editEnquiryManagement
        createOrganisation
        editOrganisation
        editOrganisationEnquiries
        accessCustomerApp
        isInternalStaff
        isAdministrator
        isAgent
        isLoginAsAgent
        listing {
          canBulkDelete
          canBulkOffline
          canBulkPublish
          canBulkReactive
          canCreateListing
          canEditPrimaryAgent
          canBatchUpload
          canFilterByAgency
          canFilterByBranch
          canFilterByAgent
          canEditLocationLevel1Id
          canEditLocationLevel2Id
          canEditLocationLevel3Text
          canEditLocationLevel5Text
          canEditLocationLevel5Id
          canEditLocationAddress
          canEditLocationPostalCode
          canEditLocationCoordinate
          canEditLocationUnit
          canEditLocationFloor
          canEditLocationBlock
          canDeleteListing
          canDowngradeListing
          canDegradeListing
          canBulkDelete
          canBulkOffline
          canBulkPublish
          canBulkReactive
          canBulkExtend
          canReAdvertiseListing
          canExportListing
        }
        announcement {
          canManageAnnouncement
          canViewManageAnnouncement
        }
        allocation {
          canViewAllocationMenu
          canTransferAllocation
        }
        report {
          canDownloadPerformanceReport
          canViewSpecialistReport
          canDownloadSpecialistReport
        }
        qualityIssue {
          canEditQualityIssue
          canViewQualityIssue
        }
        viewWatermarkMenu
        settings {
          canViewWatermark
          canEditWatermark
        }
        lead {
          canEditLead
          canViewLead
          canViewInbox
        }
        agentools {
          canEditAgentools
          canViewAgentools
        }
        propertyWanted {
          canViewPropertyWanted
        }
        fsboAlert {
          canViewFsboAlert
          canEditFsboAlert
        }
        dashboard {
          canViewDashboard
        }
        reAdvertiseScheduler {
          canCreateReAdvertiseScheduler
          canDeleteReAdvertiseScheduler
          canViewReAdvertiseScheduler
        }
        specialist {
          canAccessMenu

          canViewSlot
          canEditSlot
          canViewAreaAndCondoSpecialist
          canEditAreaAndCondoSpecialist
          canOfflineSpecialist
          canActivateSpecialist
          canDeleteSpecialist
        }
        selfServe {
          canExportPaymentHistory
          canViewPaymentHistory
          canAccessCreditBrickz
        }
        dynamicPricing {
          canEditDynamicPricing
          canViewDynamicPricing
        }
      }
    }
  }
`; // when append field in this query, remember to append in cypress user query as well to avoid test error

type IdType = string | number | null;

interface MultiLangText {
  en_GB: string;
  zh_CN: string;
  zh_HK: string;
}

/* manually create GraphQL types for user here, as it doesn't change frequently */
interface ViewerQueryType {
  viewer: {
    id: string;
    avatar: string | null;
    organisationId: number | null;
    email: string | null;
    emailVerified: boolean;
    lockeUserName: string;
    spotReaId: string | null;
    firstName: MultiLangText | null;
    lastName: MultiLangText | null;
    fullName: MultiLangText;
    hasContract: boolean;
    createdDate: string | null;
    isToggledLCv3: boolean;
    featureToggle: {
      isListingV3FlowOpted: boolean;
      isListingManagementV3WebEnabled: boolean;
    };
    accountManager: {
      avatar: string | null;
      displayName: string | null;
      email: string | null;
      contact: {
        agentEmail: string | null;
        consumerEmail: string | null;
        primaryPhone: {
          number: string | null;
          isWhatsApp: boolean;
          phoneCode: number | null;
          phoneCodeReference: {
            id: IdType;
            code: number | null;
            description: string | null;
            label: string | null;
          };
        };
        secondaryPhone: {
          number: string | null;
          isWhatsApp: boolean;
          phoneCode: number | null;
          phoneCodeReference: {
            id: IdType;
            code: number | null;
            description: string | null;
            label: string | null;
          };
        };
        didPhone: {
          number: string | null;
          isWhatsApp: boolean;
          phoneCode: number | null;
          phoneCodeReference: {
            id: IdType;
            code: number | null;
            description: string | null;
            label: string | null;
          };
        };
      };
    };
    organisation: {
      id: IdType;
      name: MultiLangText;
      isAgency: boolean;
      isBranch: boolean;
      allocationOn: boolean;
      parent: {
        id: IdType;
        name: MultiLangText;
        allocationOn: boolean;
      };
      hasContract: boolean;
    } | null;
    roleCodes: Array<{
      id: string;
      code: number;
      description: string;
      label: string;
    }> | null;
    capabilityPermission: {
      canAccessBrickz: boolean;
      canAccessIqAgent: boolean;
      brickzLatestExpiryDate: string;
    };
    capability: {
      isAgentNetUser: boolean;
    };
    permissions: {
      canCreateRoleCodes: Array<{
        id: string;
        code: number;
        description: string;
      }>;
      viewMyTeamMenu: boolean;
      viewOrganisationMenu: boolean;
      viewSubscriptionsMenu: boolean;
      viewEnquiryManagementMenu: boolean;
      editEnquiryManagement: boolean;
      createOrganisation: boolean;
      editOrganisation: boolean;
      editOrganisationEnquiries: boolean;
      accessCustomerApp: boolean;
      isInternalStaff: boolean;
      isAdministrator: boolean;
      isAgent: boolean;
      isLoginAsAgent: boolean;
      listing: {
        canBulkDelete: boolean;
        canBulkOffline: boolean;
        canBulkPublish: boolean;
        canBulkReactive: boolean;
        canCreateListing: boolean;
        canEditPrimaryAgent: boolean;
        canBatchUpload: boolean;
        canFilterByAgency: boolean;
        canFilterByBranch: boolean;
        canFilterByAgent: boolean;
        canEditLocationLevel1Id: boolean;
        canEditLocationLevel2Id: boolean;
        canEditLocationLevel3Text: boolean;
        canEditLocationLevel5Text: boolean;
        canEditLocationLevel5Id: boolean;
        canEditLocationAddress: boolean;
        canEditLocationPostalCode: boolean;
        canEditLocationCoordinate: boolean;
        canEditLocationUnit: boolean;
        canEditLocationFloor: boolean;
        canEditLocationBlock: boolean;
        canDeleteListing: boolean;
        canDowngradeListing: boolean;
        canDegradeListing: boolean;
        canBulkExtend: boolean;
        canReAdvertiseListing: boolean;
        canExportListing: boolean;
      };
      announcement: {
        canManageAnnouncement: boolean;
        canViewManageAnnouncement: boolean;
      };
      allocation: {
        canViewAllocationMenu: boolean;
        canTransferAllocation: boolean;
      };
      report: {
        canDownloadPerformanceReport: boolean;
        canViewSpecialistReport: boolean;
        canDownloadSpecialistReport: boolean;
      };
      qualityIssue: {
        canEditQualityIssue: boolean;
        canViewQualityIssue: boolean;
      };
      viewWatermarkMenu: boolean;
      settings: {
        canViewWatermark: boolean;
        canEditWatermark: boolean;
      };
      lead: {
        canEditLead: boolean;
        canViewLead: boolean;
        canViewInbox: boolean;
      };
      agentools: {
        canEditAgentools: boolean;
        canViewAgentools: boolean;
      };
      propertyWanted: {
        canViewPropertyWanted: boolean;
      };
      fsboAlert: {
        canViewFsboAlert: boolean;
        canEditFsboAlert: boolean;
      };
      dashboard: {
        canViewDashboard: boolean;
      };
      reAdvertiseScheduler: {
        canCreateReAdvertiseScheduler: boolean;
        canDeleteReAdvertiseScheduler: boolean;
        canViewReAdvertiseScheduler: boolean;
      };
      specialist: {
        canAccessMenu: boolean;
        canViewSlot: boolean;
        canEditSlot: boolean;
        canViewAreaAndCondoSpecialist: boolean;
        canEditAreaAndCondoSpecialist: boolean;
        canOfflineSpecialist: boolean;
        canActivateSpecialist: boolean;
        canDeleteSpecialist: boolean;
      };
      selfServe: {
        canExportPaymentHistory: boolean;
        canViewPaymentHistory: boolean;
        canAccessCreditBrickz: boolean;
      };
      dynamicPricing: {
        canEditDynamicPricing: boolean;
        canViewDynamicPricing: boolean;
      };
    };
  };
}

export interface UserProps {
  user: ViewerQueryType["viewer"];
  viewer: ViewerQueryType["viewer"];
}

const defaultUser = {
  id: "",
  organisationId: null,
  avatar: null,
  email: null,
  emailVerified: false,
  lockeUserName: "",
  spotReaId: null,
  firstName: null,
  lastName: null,
  isToggledLCv3: false,
  featureToggle: {
    isListingV3FlowOpted: false,
    isListingManagementV3WebEnabled: false,
  },
  fullName: {
    zh_HK: "",
    zh_CN: "",
    en_GB: "",
  },
  roleCodes: null,
  hasContract: true,
  createdDate: null,
  accountManager: {
    avatar: null,
    displayName: null,
    email: null,
    contact: {
      agentEmail: null,
      consumerEmail: null,
      primaryPhone: {
        number: null,
        isWhatsApp: false,
        phoneCode: null,
        phoneCodeReference: {
          id: null,
          code: null,
          description: null,
          label: null,
        },
      },
      secondaryPhone: {
        number: null,
        isWhatsApp: false,
        phoneCode: null,
        phoneCodeReference: {
          id: null,
          code: null,
          description: null,
          label: null,
        },
      },
      didPhone: {
        number: null,
        isWhatsApp: false,
        phoneCode: null,
        phoneCodeReference: {
          id: null,
          code: null,
          description: null,
          label: null,
        },
      },
    },
  },
  organisation: {
    id: null,
    name: {
      zh_HK: "",
      zh_CN: "",
      en_GB: "",
    },
    parent: {
      id: null,
      name: {
        zh_HK: "",
        zh_CN: "",
        en_GB: "",
      },
      allocationOn: false,
    },
    isAgency: false,
    isBranch: false,
    allocationOn: false,
    hasContract: true,
  },
  capabilityPermission: {
    canAccessBrickz: false,
    canAccessIqAgent: false,
    brickzLatestExpiryDate: "",
  },
  capability: {
    isAgentNetUser: false,
  },
  permissions: {
    accessCustomerApp: false,
    viewMyTeamMenu: false,
    viewOrganisationMenu: false,
    viewSubscriptionsMenu: false,
    viewEnquiryManagementMenu: false,
    editEnquiryManagement: false,
    createOrganisation: false,
    editOrganisation: false,
    editOrganisationEnquiries: false,
    canCreateRoleCodes: [],
    isInternalStaff: false,
    isAgent: false,
    isLoginAsAgent: false,
    isAdministrator: false,
    listing: {
      canCreateListing: false,
      canEditPrimaryAgent: false,
      canBatchUpload: false,
      canFilterByAgency: false,
      canFilterByBranch: false,
      canFilterByAgent: false,
      canEditLocationLevel1Id: false,
      canEditLocationLevel2Id: false,
      canEditLocationLevel3Text: false,
      canEditLocationLevel5Text: false,
      canEditLocationLevel5Id: false,
      canEditLocationAddress: false,
      canEditLocationPostalCode: false,
      canEditLocationCoordinate: false,
      canEditLocationUnit: false,
      canEditLocationFloor: false,
      canEditLocationBlock: false,
      canDeleteListing: false,
      canDowngradeListing: false,
      canDegradeListing: false,
      canBulkDelete: false,
      canBulkOffline: false,
      canBulkPublish: false,
      canBulkReactive: false,
      canBulkExtend: false,
      canReAdvertiseListing: false,
      canExportListing: false,
    },
    allocation: {
      canViewAllocationMenu: false,
      canTransferAllocation: false,
    },
    announcement: {
      canManageAnnouncement: false,
      canViewManageAnnouncement: false,
    },
    report: {
      canDownloadPerformanceReport: false,
      canViewSpecialistReport: false,
      canDownloadSpecialistReport: false,
    },
    qualityIssue: {
      canEditQualityIssue: false,
      canViewQualityIssue: false,
    },
    viewWatermarkMenu: true,
    settings: {
      canViewWatermark: true,
      canEditWatermark: true,
    },
    lead: {
      canEditLead: false,
      canViewLead: false,
      canViewInbox: false,
    },
    agentools: {
      canEditAgentools: false,
      canViewAgentools: false,
    },
    propertyWanted: {
      canViewPropertyWanted: false,
    },
    fsboAlert: {
      canEditFsboAlert: false,
      canViewFsboAlert: false,
    },
    dashboard: {
      canViewDashboard: false,
    },
    reAdvertiseScheduler: {
      canCreateReAdvertiseScheduler: false,
      canDeleteReAdvertiseScheduler: false,
      canViewReAdvertiseScheduler: true,
    },
    specialist: {
      canAccessMenu: false,
      canViewSlot: false,
      canEditSlot: false,
      canViewAreaAndCondoSpecialist: false,
      canEditAreaAndCondoSpecialist: false,
      canOfflineSpecialist: false,
      canActivateSpecialist: false,
      canDeleteSpecialist: false,
    },
    selfServe: {
      canExportPaymentHistory: false,
      canViewPaymentHistory: false,
      canAccessCreditBrickz: false,
    },
    dynamicPricing: {
      canEditDynamicPricing: false,
      canViewDynamicPricing: false,
    },
  },
};
const defaultContext = {
  viewer: defaultUser,
  user: defaultUser,
};

const UserContext = React.createContext<UserProps>(defaultContext);

const UserConsumer = UserContext.Consumer;

interface UserProviderProps {
  pollingInterval?: number;
  locale: string;
  onRequestCompleted?: (data: {} | ViewerQueryType) => void;
  onRequestError?: (error: ApolloError) => void;
}
interface UserContextProviderProps {
  error: ApolloError | undefined;
  data: ViewerQueryType | undefined;
  defaultContext: UserProps;
}

const UserContextProvider: React.FC<UserContextProviderProps> = (props) => {
  const { error, data, defaultContext } = props;
  const value = React.useMemo(() => {
    if (error) return defaultContext;
    return { user: data?.viewer!, viewer: data?.viewer! };
  }, [error, data, defaultContext]);

  return (
    <UserContext.Provider value={value}>{props.children}</UserContext.Provider>
  );
};

const UserProvider: React.FC<UserProviderProps> = (props) => (
  <LegacyQuery<ViewerQueryType>
    query={userQuery}
    context={{ headers: { "accept-language": props.locale } }}
    onCompleted={props.onRequestCompleted}
    onError={props.onRequestError}
  >
    {({ data, loading, error }) =>
      loading || (error && /401/.test(error.message)) ? null : (
        <UserContextProvider
          defaultContext={defaultContext}
          error={error}
          data={data}
        >
          {props.children}
        </UserContextProvider>
      )
    }
  </LegacyQuery>
);

const UserProvider2: React.FC<UserProviderProps> = (props) => {
  const { data, loading, error } = useQuery<ViewerQueryType>(userQuery, {
    context: { headers: { "accept-language": props.locale } },
    onCompleted: props.onRequestCompleted,
    onError: props.onRequestError,
  });

  return loading || (error && /401/.test(error.message)) ? null : (
    <UserContextProvider
      defaultContext={defaultContext}
      error={error}
      data={data}
    >
      {props.children}
    </UserContextProvider>
  );
};

function withUser<P extends UserProps = UserProps>(
  WrappedComponent: React.ComponentType<P>
): React.ComponentType<Omit<P, keyof UserProps>> {
  return (props) => (
    <UserConsumer>
      {(context) => (
        <WrappedComponent
          {...(props as P)}
          user={context.user}
          viewer={context.viewer}
        />
      )}
    </UserConsumer>
  );
}

function useUser() {
  const userContext = React.useContext(UserContext);
  return userContext.user;
}
export { UserProvider, UserProvider2, withUser, useUser };
